import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getBranding, updateBranding, uploadBrandingLogo, getTimezones, getHhbWindowTest } from "../../lib/api";
import { triggerBlobDownload } from "../../lib/download";

export default function SchoolBrandingTab() {
  const [form, setForm] = useState({
    school_name: "", tagline: "", motto: "",
    primary_color: "#1F2E5C", accent_color: "#C84E2A",
    report_footer: "", contact_email: "", timezone: "America/New_York",
    school_address: "", school_phone: "", school_website: "",
    school_city: "", school_state: "", school_zip: "",
    hhb_signer_name: "Elisa Walker", hhb_signer_credentials: "M.Ed.",
    hhb_signer_title: "School Director", hhb_investigation_days: 10,
    hhb_window_left_offset: 0, hhb_window_top_offset: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoSrc, setLogoSrc] = useState("");
  const [timezones, setTimezones] = useState(["America/New_York"]);

  const refreshLogo = () => {
    setLogoSrc(`${process.env.REACT_APP_BACKEND_URL}/api/branding/logo?t=${Date.now()}`);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getBranding();
        setForm((p) => ({ ...p, ...res.data, timezone: res.data.timezone || "America/New_York" }));
        refreshLogo();
        try {
          const tz = await getTimezones();
          setTimezones(tz.data?.timezones || ["America/New_York"]);
        } catch { /* keep default list */ }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load branding");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleWindowTest = async () => {
    try {
      const res = await getHhbWindowTest();
      triggerBlobDownload(res.data, "HHB_window_alignment_test.pdf");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate test page");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBranding(form);
      toast.success("Branding saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadBrandingLogo(file);
      toast.success("Logo updated");
      refreshLogo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading) return <Card><CardContent className="py-8 text-center">Loading…</CardContent></Card>;

  return (
    <Card data-testid="school-branding-tab">
      <CardHeader>
        <CardTitle>School Branding</CardTitle>
        <CardDescription>
          These settings control the school name, logo, colors, and footer text used across the app and on Progress Report PDFs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div className="md:col-span-1 space-y-3">
            <Label>Logo</Label>
            <div className="border rounded-md p-4 flex items-center justify-center bg-black/5" style={{ minHeight: 120 }}>
              {logoSrc ? <img src={logoSrc} alt="logo" style={{ maxHeight: 100, maxWidth: "100%" }} /> : <span className="text-xs text-muted-foreground">No logo</span>}
            </div>
            <label className="inline-block">
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="branding-logo-input" />
              <Button asChild variant="outline" disabled={uploading}>
                <span>{uploading ? "Uploading…" : "Upload Logo"}</span>
              </Button>
            </label>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>School Name</Label>
              <Input value={form.school_name || ""} onChange={(e) => setForm((p) => ({ ...p, school_name: e.target.value }))} data-testid="branding-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Tagline (subtitle)</Label>
              <Input value={form.tagline || ""} onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))} data-testid="branding-tagline-input" />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Motto (italicized under the name)</Label>
              <Input value={form.motto || ""} onChange={(e) => setForm((p) => ({ ...p, motto: e.target.value }))} placeholder='e.g., "Non Agere Culus"' data-testid="branding-motto-input" />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Time Zone</Label>
              <Select value={form.timezone || "America/New_York"} onValueChange={(v) => setForm((p) => ({ ...p, timezone: v }))}>
                <SelectTrigger data-testid="branding-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz.replace("America/", "").replace("Pacific/", "").replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Attendance check-in/tardy times and the nightly jobs run in this zone.</p>
            </div>
            <div className="space-y-1">
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color || "#1F2E5C"} onChange={(e) => setForm((p) => ({ ...p, primary_color: e.target.value }))} className="h-10 w-14 rounded border" data-testid="branding-primary-color" />
                <Input value={form.primary_color || ""} onChange={(e) => setForm((p) => ({ ...p, primary_color: e.target.value }))} className="font-mono" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Accent Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.accent_color || "#C84E2A"} onChange={(e) => setForm((p) => ({ ...p, accent_color: e.target.value }))} className="h-10 w-14 rounded border" data-testid="branding-accent-color" />
                <Input value={form.accent_color || ""} onChange={(e) => setForm((p) => ({ ...p, accent_color: e.target.value }))} className="font-mono" />
              </div>
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>School Address (letterhead)</Label>
              <Input value={form.school_address || ""} onChange={(e) => setForm((p) => ({ ...p, school_address: e.target.value }))} placeholder="123 Main St, Townsville, VT 05000" data-testid="branding-address-input" />
            </div>
            <div className="space-y-1">
              <Label>School Phone (letterhead)</Label>
              <Input value={form.school_phone || ""} onChange={(e) => setForm((p) => ({ ...p, school_phone: e.target.value }))} placeholder="(802) 555-0100" data-testid="branding-phone-input" />
            </div>
            <div className="space-y-1">
              <Label>School Website (letterhead)</Label>
              <Input value={form.school_website || ""} onChange={(e) => setForm((p) => ({ ...p, school_website: e.target.value }))} placeholder="www.yourschool.org" data-testid="branding-website-input" />
            </div>
            <div className="space-y-1">
              <Label>City</Label>
              <Input value={form.school_city || ""} onChange={(e) => setForm((p) => ({ ...p, school_city: e.target.value }))} data-testid="branding-city-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>State</Label>
                <Input value={form.school_state || ""} onChange={(e) => setForm((p) => ({ ...p, school_state: e.target.value }))} data-testid="branding-state-input" />
              </div>
              <div className="space-y-1">
                <Label>ZIP</Label>
                <Input value={form.school_zip || ""} onChange={(e) => setForm((p) => ({ ...p, school_zip: e.target.value }))} data-testid="branding-zip-input" />
              </div>
            </div>
            <div className="md:col-span-2 border-t pt-3 mt-1">
              <p className="text-sm font-semibold text-[#00305E]">HHB Letter Signer &amp; Timeline</p>
              <p className="text-xs text-muted-foreground">Used on Hazing/Harassment/Bullying notification letters.</p>
            </div>
            <div className="space-y-1">
              <Label>Signer Name</Label>
              <Input value={form.hhb_signer_name || ""} onChange={(e) => setForm((p) => ({ ...p, hhb_signer_name: e.target.value }))} data-testid="branding-signer-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Signer Credentials</Label>
              <Input value={form.hhb_signer_credentials || ""} onChange={(e) => setForm((p) => ({ ...p, hhb_signer_credentials: e.target.value }))} placeholder="M.Ed." data-testid="branding-signer-creds-input" />
            </div>
            <div className="space-y-1">
              <Label>Signer Title</Label>
              <Input value={form.hhb_signer_title || ""} onChange={(e) => setForm((p) => ({ ...p, hhb_signer_title: e.target.value }))} placeholder="School Director" data-testid="branding-signer-title-input" />
            </div>
            <div className="space-y-1">
              <Label>Investigation Timeline (school days)</Label>
              <Input type="number" min={1} value={form.hhb_investigation_days || 10} onChange={(e) => setForm((p) => ({ ...p, hhb_investigation_days: e.target.value }))} data-testid="branding-investigation-days-input" />
            </div>
            <div className="md:col-span-2 border-t pt-3 mt-1">
              <p className="text-sm font-semibold text-[#00305E]">#10 Window-Envelope Fold Alignment</p>
              <p className="text-xs text-muted-foreground">Nudge the address placement on the "Letters with address page" print option so both addresses line up in a standard #10 double-window envelope when the sheet is tri-folded. Positive values move the text right / down.</p>
            </div>
            <div className="space-y-1">
              <Label>Left offset (inches)</Label>
              <Input type="number" step="0.05" value={form.hhb_window_left_offset ?? 0} onChange={(e) => setForm((p) => ({ ...p, hhb_window_left_offset: e.target.value }))} data-testid="branding-window-left-offset-input" />
            </div>
            <div className="space-y-1">
              <Label>Top offset (inches)</Label>
              <Input type="number" step="0.05" value={form.hhb_window_top_offset ?? 0} onChange={(e) => setForm((p) => ({ ...p, hhb_window_top_offset: e.target.value }))} data-testid="branding-window-top-offset-input" />
            </div>
            <div className="md:col-span-2">
              <Button type="button" variant="outline" size="sm" onClick={handleWindowTest} data-testid="branding-window-test-btn">
                Download window alignment test
              </Button>
              <p className="text-xs text-muted-foreground mt-1">Save your offsets first, then print this test page double-sided and fold it to check the fit.</p>
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Report Footer Text (appears at bottom of PDFs)</Label>
              <Input value={form.report_footer || ""} onChange={(e) => setForm((p) => ({ ...p, report_footer: e.target.value }))} placeholder="© 2025 The Mill School • Confidential" data-testid="branding-footer-input" />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Contact Email (shown in portal welcome / login emails)</Label>
              <Input type="email" value={form.contact_email || ""} onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))} placeholder="e.g. admin@yourschool.org" data-testid="branding-contact-email-input" />
              <p className="text-xs text-muted-foreground">Used as the "reach out anytime" and Android-access contact address in the welcome email. Leave blank to use your first admin account.</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="branding-save-btn">
            {saving ? "Saving…" : "Save Branding"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
